import { useState, isValidElement, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { NavButton, NavButtonProps } from '@components/button';
import { Divider } from '@components/divider';
import { DropdownTemplate, DropdownTemplateProps } from '@components/dropdown/DropdownTemplate';
import { Logo, LogoProps } from '@components/nav/Logo';
import { NavItem, NavItemProps } from '@components/nav/NavItem';
import { NavMenu, NavMenuProps } from '@components/nav/NavMenu';
import { useCollapseOnOverflow } from './useCollapseOnOverflow';
import './PrimaryNav.scss';

export interface PrimaryNavProps {
  brand?: LogoProps['brand'];
  hideLinks?: boolean;
  hideMenu?: boolean;
  hideLogo?: boolean;
  black?: boolean;
  scrolled?: boolean;
  navButtonProps?: NavButtonProps;
  navItems?: Array<NavItemProps> | Array<React.ReactNode>;
  navActionLinkProps?: NavItemProps;
  /** Hides the `navActionLinkProps` link in the mobile layout below 768px */
  hideActionLinkOnMobile?: boolean;
  profileDropdown?: DropdownTemplateProps | React.ReactNode;
  hasDivider?: boolean;
  navMenuProps?: NavMenuProps;
  isSticky?: boolean;
  handleLogoClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  logoSlot?: React.ReactNode | ((logoSize: LogoSize) => React.ReactNode);
  logoHref?: string;
  className?: string;
  navAriaLabel?: string;
  logoAriaLabel?: string;
  /** Called when the nav switches between the desktop links and the mobile menu layout */
  onMobileViewChange?: (isMobileView: boolean) => void;
}

export type LogoSize = 'small' | 'large';

const LOGO_ARIA_LABEL_BY_BRAND = {
  pplus: 'paramount plus',
  pluto: 'Pluto TV Home',
};

const COLLAPSED_CLASS_NAME = 'vds-nav-main--collapsed';
const DESKTOP_HIDDEN_CLASS_NAME = 'vds-nav-main--desktop-hidden';
const MOBILE_BREAKPOINT = 1120;
const MOBILE_MEDIA_QUERY = `(max-width: ${MOBILE_BREAKPOINT - 1}px)`;

const getMobileMediaQueryList = () =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(MOBILE_MEDIA_QUERY)
    : undefined;

const getIsBelowBreakpoint = () => getMobileMediaQueryList()?.matches ?? false;

/**
 * PrimaryNav (top/header navigation) component
 * @param brand - sets brand pplus or pluto
 * @param hideLinks - override to hide navItems
 * @param hideMenu - hide mobile hamburger menu button
 * @param hideLogo - override to hide logo
 * @param black - remove background gradient and set to black
 * @param scrolled - trigger scrolled animation
 * @param navButtonProps - props for optional NavButton
 * @param navItems - main navigation links or components to render in this slot
 * @param navActionLinkProps - props for optional navigation action link (like Sign In)
 * @param hideActionLinkOnMobile - hide the navActionLinkProps link in the mobile layout below 768px. default false
 * @param profileDropdown - props for user profile dropdown OR component to render in this slot
 * @param hasDivider - show vertical divider next to site logo
 * @param navMenuProps - props for NavMenu
 * @param isSticky - set to false to disable sticky behavior. default true
 * @param handleLogoClick - click event callback for the logo. Only used when no custom logoSlot is provided.
 * @param logoSlot - custom logo node or render function for full logo slot. When provided, the default logo
 *   wrapper (link, aria-label, tabindex, click handler) is not applied; the consumer is responsible for
 *   setting href, aria-label, tabindex, and click behavior on their custom logo for accessibility and navigation.
 * @param logoHref - href for the logo link (default /home). Only used when no custom logoSlot is provided.
 * @param className - additional class name *
 * @param navAriaLabel - aria-label for the navigation wrapper
 * @param logoAriaLabel - aria-label for the logo. Only used when no custom logoSlot is provided.
 * @param onMobileViewChange - Called when the nav switches between the desktop links and the mobile menu layout
 */
export const PrimaryNav: React.FC<PrimaryNavProps> = ({
  brand = 'pplus',
  hideLinks,
  hideMenu,
  hideLogo = false,
  black,
  scrolled,
  navButtonProps,
  navMenuProps,
  navItems = [],
  profileDropdown,
  hasDivider,
  isSticky = true,
  handleLogoClick,
  logoSlot,
  logoHref = '/home',
  className,
  navActionLinkProps,
  hideActionLinkOnMobile = false,
  navAriaLabel = 'Primary',
  logoAriaLabel = LOGO_ARIA_LABEL_BY_BRAND[brand],
  onMobileViewChange,
}) => {
  const [isActive, setIsActive] = useState(false);
  const [isBelowBreakpoint, setIsBelowBreakpoint] = useState(getIsBelowBreakpoint);
  const anchorRef = useRef<HTMLAnchorElement | null>(null);
  const navRef = useRef<HTMLElement | null>(null);
  const desktopRef = useRef<HTMLDivElement | null>(null);
  const isCollapsed = useCollapseOnOverflow(navRef, desktopRef, DESKTOP_HIDDEN_CLASS_NAME);
  const isDesktopHidden = isCollapsed || isActive;
  const isMobileView = isBelowBreakpoint || isDesktopHidden;
  const onMobileViewChangeRef = useRef(onMobileViewChange);

  onMobileViewChangeRef.current = onMobileViewChange;

  useEffect(() => {
    const mediaQueryList = getMobileMediaQueryList();
    if (!mediaQueryList) return;

    const handleChange = (event: MediaQueryListEvent) => setIsBelowBreakpoint(event.matches);

    setIsBelowBreakpoint(mediaQueryList.matches);
    mediaQueryList.addEventListener('change', handleChange);

    return () => mediaQueryList.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    if (!isBelowBreakpoint) setIsActive(false);
  }, [isBelowBreakpoint]);

  useEffect(() => {
    onMobileViewChangeRef.current?.(isMobileView);
  }, [isMobileView]);

  const handleMenuClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    setIsActive((prevState: boolean) => !prevState);
    if (navMenuProps?.onClick) navMenuProps.onClick(e);
  };

  const navLinks = navItems.map((item, index) => {
    if (isValidElement(item)) {
      return <li key={`nav-item-${index}`}>{item as React.ReactNode}</li>;
    } else if (typeof item === 'object') {
      return (
        <li key={`nav-item-${index}`}>
          <NavItem size="small" {...item} />
        </li>
      );
    }
  });

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    if (handleLogoClick) handleLogoClick(e);
    if (anchorRef && anchorRef.current) window.location.href = anchorRef.current.href;
  };

  const logo = (logoSize: LogoSize) => {
    if (logoSlot) {
      return typeof logoSlot === 'function' ? logoSlot(logoSize) : logoSlot;
    }

    return (
      <a
        tabIndex={0}
        ref={anchorRef}
        onClick={handleClick}
        href={logoHref}
        aria-label={logoAriaLabel}
        className="vds-nav-main__logo-wrapper vds-link">
        <Logo size={logoSize} brand={brand} />
      </a>
    );
  };

  let userDropdown = null;

  // profileDropdown can be either DropdownTemplateProps, or a React component
  if (profileDropdown) {
    const dropdownContent = isValidElement(profileDropdown) ? (
      profileDropdown
    ) : (
      <DropdownTemplate {...(profileDropdown as DropdownTemplateProps)} />
    );
    userDropdown = <div className="vds-nav-main__dropdown">{dropdownContent}</div>;
  }

  return (
    <header>
      <nav
        ref={navRef}
        aria-label={navAriaLabel}
        className={classNames('vds-nav-main', className, {
          'vds-nav-main--hide-action-link-mobile': hideActionLinkOnMobile,
          'vds-nav-main--black': black || isActive,
          'vds-nav-main--scrolled': scrolled,
          'vds-nav-main--sticky': isSticky,
          [COLLAPSED_CLASS_NAME]: isCollapsed,
          [DESKTOP_HIDDEN_CLASS_NAME]: isDesktopHidden,
        })}>
        <div ref={desktopRef} className="vds-nav-main--desktop">
          <div className="vds-nav-main--left">
            {!hideLogo && logo('large')}
            {hasDivider && <Divider vertical variant="secondary" />}
            {!hideLinks && <ul className="vds-nav-main__links">{navLinks}</ul>}
          </div>
          <div className="vds-nav-main--right">
            {userDropdown}
            {navActionLinkProps && (
              <NavItem
                size="small"
                {...navActionLinkProps}
                className={classNames('vds-nav-main__action-link', navActionLinkProps.className)}
              />
            )}
            {navButtonProps && (
              <NavButton
                {...navButtonProps}
                size="large"
                className={classNames('vds-nav-main__action-button', navButtonProps.className)}
              />
            )}
          </div>
        </div>
        <div className="vds-nav-main--mobile">
          <div className="vds-nav-main--left">
            {!hideMenu && (
              <NavMenu
                isActive={isActive}
                onClick={handleMenuClick}
                activeLabel={navMenuProps?.activeLabel}
                defaultLabel={navMenuProps?.defaultLabel}
                brand={brand}
              />
            )}
            {!hideLogo && logo('small')}
          </div>
          <div className="vds-nav-main--right">
            {navActionLinkProps && (
              <NavItem
                size="small"
                {...navActionLinkProps}
                className={classNames('vds-nav-main__action-link', navActionLinkProps.className)}
              />
            )}
            {navButtonProps && (
              <NavButton
                {...navButtonProps}
                size="small"
                className={classNames('vds-nav-main__action-button', navButtonProps.className)}
              />
            )}
          </div>
        </div>
      </nav>
    </header>
  );
};
